import { TraitLabel } from '@fluid-experimental/tree';
import { MfsHost } from '../mfsHost';
import { MfsRelationship, MfsRelationshipFilter, RelationshipKind } from '../mfsTypes';

export type EdgeInitial = '#e';
export type EdgePrefixType = `${EdgeInitial}.${MfsRelationship['direction']}.${RelationshipKind}`;
export type EdgeLabelType = `${EdgePrefixType}.${MfsRelationship['type']}`;

export const EDGE_PREFIX: EdgeInitial = '#e';
export const OUTGOING_TWO_WAY_EDGE: EdgePrefixType = `${'#e'}.${'out'}.${RelationshipKind.TwoWay}`;
export const INCOMING_TWO_WAY_EDGE: EdgePrefixType = `${'#e'}.${'in'}.${RelationshipKind.TwoWay}`;

function isEdgeLabelType(input: string): input is EdgeLabelType {
  return input.startsWith(EDGE_PREFIX);
}

/** Returns the label of the given `relationship`. */
function getLabelOf(relationship: MfsRelationship): EdgePrefixType {
  return `${'#e'}.${relationship.direction}.${relationship.kind ?? 0}`;
}

/** Returns the unique label of the given `relationship`. */
export function getUniqueLabelOf(app: MfsHost, relationship: MfsRelationship): EdgeLabelType {
  return `${getLabelOf(relationship)}.${relationship.type}`;
}

/** Returns a new `MfsRelation` object by reversing the direction of `relation` preserving the attributes if any. */
export const reverseDirectionOf = (relation: MfsRelationship): MfsRelationship => {
  return {
    type: relation.type,
    direction: relation.direction === 'out' ? 'in' : 'out',
    kind: relation.kind,
    ...(relation.attributes && { attributes: relation.attributes }),
  };
};

/** Memoized edges per {@link EdgeLabelType}. */
const edgesCache = new Map<EdgeLabelType, Omit<MfsRelationship, 'attributes'>>();

/** @returns An {@link MfsRelationship} given relationship key & additional attributes. */
export function toMfsRelationship(key: EdgeLabelType, attributes?: MfsRelationship['attributes']): MfsRelationship {
  let edge = edgesCache.get(key);

  if (!edge) {
    edge = parseEdgeLabel(key);
    edgesCache.set(key, edge);
  }

  return { ...edge, ...(attributes && { attributes }) };
}

/** Parses the edge label as MfsRelationship. */
export function parseEdgeLabel(label: EdgeLabelType): Required<Omit<MfsRelationship, 'attributes'>> {
  const parts = label.split('.');

  return {
    direction: parts[1] as MfsRelationship['direction'],
    kind:
      (parts[2] as unknown as number) == RelationshipKind.OneWay ? RelationshipKind.OneWay : RelationshipKind.TwoWay,
    type: parts[3],
  };
}

/** Returns an edge trait label filter based on the given filter. */
export function getEdgeLabelFilter(filter?: MfsRelationshipFilter): (edgeLabel: TraitLabel) => boolean {
  return (edgeLabel) => {
    if (!isEdgeLabelType(edgeLabel)) {
      return false;
    }

    if (!filter) {
      return true;
    }

    const relationship = parseEdgeLabel(edgeLabel);

    return (
      (!filter.matchDirection || filter.matchDirection(relationship.direction)) &&
      (!filter.matchKind || filter.matchKind(relationship.kind)) &&
      (!filter.matchType || filter.matchType(relationship.type))
    );
  };
}
